import { useEffect, useId, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import type { TenantSession } from '../lib/session'
import {
  listPortalUsers,
  removePortalUser,
  requestTenantLink,
  savePortalUser,
  setDesignatedContact,
  tenantCan,
  type MeResponse,
  type PortalUser,
  type TenantAccessLevel,
} from '../lib/api'
import { isLastFullAccess } from '../lib/tenantUsers'
import { ghostBtn, purpleBtn } from '../ui'
import { TenantModal } from '../components/TenantModal'

const LEVELS: TenantAccessLevel[] = ['full', 'operational', 'validation']
const LEVEL_LABEL: Record<TenantAccessLevel, string> = {
  full: 'Full access',
  operational: 'Operational',
  validation: 'Validation-only',
}
const LEVEL_BLURB: Record<TenantAccessLevel, string> = {
  full: 'Everything: parkers, permits, validations, billing, and user management.',
  operational: 'Parkers, permits, and validations. No billing or user management.',
  validation: 'View and issue validations. Nothing else.',
}

function initials(user: PortalUser): string {
  const source = user.name?.trim() || user.email.split('@')[0] || '?'
  return source.split(/\s+/).slice(0, 2).map(s => s.charAt(0)).join('').toUpperCase()
}

function resolvedLevel(user: PortalUser): TenantAccessLevel {
  return user.accessLevel ?? 'full'
}

type InviteField = 'name' | 'email'

interface Props {
  session: TenantSession
  me: MeResponse
  onClose: () => void
  onSelfAccessChanged: () => Promise<void>
}

export function UsersPanel({ session, me, onClose, onSelfAccessChanged }: Props) {
  const manage = tenantCan(me, 'users.manage')
  const [users, setUsers] = useState<PortalUser[]>([])
  const [loading, setLoading] = useState(true)
  // A failed read of the list is kept apart from a refused change: without the
  // list there is nothing to change, so the panel shows only the failure and
  // Retry, while a refused change shows above a list that is still there.
  const [loadError, setLoadError] = useState('')
  const [error, setError] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [level, setLevel] = useState<TenantAccessLevel>('operational')
  const [busy, setBusy] = useState('')
  const [notice, setNotice] = useState('')
  const [removeUser, setRemoveUser] = useState<PortalUser | null>(null)
  // Each dialog keeps its own errors, so one never also shows in the panel behind it.
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<InviteField, string>>>({})
  const [inviteError, setInviteError] = useState('')
  const [removeError, setRemoveError] = useState('')
  const nameRef = useRef<HTMLInputElement>(null)

  const load = async () => {
    setLoading(true); setLoadError('')
    try { setUsers(await listPortalUsers(session.token)) }
    catch (e) { setLoadError(e instanceof Error ? e.message : 'Could not load portal users.') }
    finally { setLoading(false) }
  }

  useEffect(() => { void load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const meUser = useMemo(
    () => users.find(u => u.email.toLowerCase() === session.email.toLowerCase()),
    [users, session.email],
  )
  const designatedUser = useMemo(
    () => users.find(u => u.isDesignatedContact),
    [users],
  )

  // Also clears here: a save refused after Invite was closed mid-flight must not
  // greet the next open with its error.
  const openInvite = () => { setInviteOpen(true); setNotice(''); setInviteError(''); setFieldErrors({}) }
  const closeInvite = () => { setInviteOpen(false); setFieldErrors({}); setInviteError('') }
  const closeRemove = () => { setRemoveUser(null); setRemoveError('') }

  const invite = async () => {
    const cleanEmail = email.trim().toLowerCase()
    const errors: Partial<Record<InviteField, string>> = {}
    if (!name.trim()) errors.name = 'Required'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) errors.email = 'Needs a valid email address'
    setFieldErrors(errors)
    if (errors.name || errors.email) return
    setBusy('invite'); setInviteError(''); setNotice('')
    try {
      const saved = await savePortalUser(session.token, { name: name.trim(), email: cleanEmail, accessLevel: level })
      setUsers(prev => [...prev.filter(u => u.email.toLowerCase() !== saved.email.toLowerCase()), saved]
        .sort((a, b) => a.email.localeCompare(b.email)))
      closeInvite(); setName(''); setEmail(''); setLevel('operational')
      // The public request-link route answers the same way whether it mailed,
      // skipped or was refused by the provider (that opacity is what keeps it
      // from confirming who has an account). A resolved request therefore
      // proves only that we asked, so the notice says exactly that.
      try {
        await requestTenantLink(cleanEmail)
        setNotice(`${cleanEmail} was added and a sign-in link was requested. If it does not arrive, they can request one from the sign-in page.`)
      } catch {
        setNotice(`${cleanEmail} was added, but the email could not be sent. They can request a sign-in link from the portal.`)
      }
    } catch (e) { setInviteError(e instanceof Error ? e.message : 'Could not add this user.') }
    finally { setBusy('') }
  }

  const changeLevel = async (user: PortalUser, next: TenantAccessLevel) => {
    if (next === resolvedLevel(user)) return
    setBusy(user.email); setError(''); setNotice('')
    try {
      const saved = await savePortalUser(session.token, { email: user.email, name: user.name, accessLevel: next })
      setUsers(prev => prev.map(u => u.email === saved.email ? saved : u))
      if (user.email.toLowerCase() === session.email.toLowerCase()) {
        await onSelfAccessChanged()
      }
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not change this access level.') }
    finally { setBusy('') }
  }

  const remove = async () => {
    if (!removeUser) return
    setBusy(removeUser.email); setRemoveError(''); setNotice('')
    try {
      await removePortalUser(session.token, removeUser.email)
      setUsers(prev => prev.filter(u => u.email !== removeUser.email))
      const removedSelf = removeUser.email.toLowerCase() === session.email.toLowerCase()
      closeRemove()
      if (removedSelf) window.dispatchEvent(new CustomEvent('parkify:session-expired'))
    } catch (e) { setRemoveError(e instanceof Error ? e.message : 'Could not remove this user.') }
    finally { setBusy('') }
  }

  const changeDesignatedContact = async (nextEmail: string) => {
    setBusy('contact'); setError(''); setNotice('')
    try {
      const selected = nextEmail || null
      await setDesignatedContact(session.token, selected)
      setUsers(prev => prev.map(u => ({
        ...u,
        isDesignatedContact: selected !== null && u.email.toLowerCase() === selected.toLowerCase(),
      })))
      setNotice(selected
        ? `${users.find(u => u.email === selected)?.name || selected} is now the designated contact.`
        : 'The contact on file is now the designated contact.')
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not change the designated contact.') }
    finally { setBusy('') }
  }

  const title = manage ? (users.length === 1 ? 'Access' : 'Manage access') : 'Your access'
  const sub = manage
    ? (users.length === 1 ? 'You are the only person who can sign in. Invite someone to share the work.' : `Who can sign in to ${me.tenant.name}, and what each person can do.`)
    : 'Your access level controls what you can see and change in this portal.'
  const removeName = removeUser ? (removeUser.name || removeUser.email) : ''

  return (
    <>
      <TenantModal
        wide={manage}
        title={title}
        sub={sub}
        onClose={onClose}
        footer={manage && !loading ? <>
          <button type="button" onClick={onClose} style={secondaryBtn}>Done</button>
          {!loadError && <button type="button" onClick={openInvite} style={primaryBtn}>Invite someone</button>}
        </> : undefined}
      >
        {error && <div style={errorBox}>{error}</div>}
        {notice && <div style={noticeBox}>{notice}</div>}
        {loading ? (
          <div style={empty}>Loading access…</div>
        ) : loadError ? (
          // The same banner and Retry as Parkers' failed reads.
          <div role="alert" style={loadErrorBanner}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#b91c1c', marginBottom: 2 }}>Couldn't load access</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>{loadError}</div>
            </div>
            <button type="button" onClick={() => void load()} style={purpleBtn}>Retry</button>
          </div>
        ) : !manage ? (
          meUser ? <ReadOnlyAccess user={meUser} me={me} /> : <div style={empty}>Your user record could not be loaded.</div>
        ) : (
          <>
            <div style={{ borderTop: '1px solid var(--color-gray-200)' }}>
              {users.map(user => {
                const last = isLastFullAccess(users, user.email)
                const self = user.email.toLowerCase() === session.email.toLowerCase()
                const rowBusy = busy === user.email
                return (
                  <div key={user.email} className="tenant-user-row" style={userRow}>
                    <Avatar text={initials(user)} />
                    <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                        {user.name || user.email.split('@')[0]}
                        {user.isDesignatedContact && <span style={contactPill}>Contact</span>}
                        {!user.lastLoginAt && <span style={pill}>Invited</span>}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--color-gray-600)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</div>
                    </div>
                    {last || self ? (
                      <div style={{ flex: '1 1 220px', fontSize: 11, lineHeight: 1.4, fontWeight: 700, color: 'var(--color-gray-600)', textAlign: 'right' }}>
                        {last
                          ? <>Only Full-access user — {users.length === 1 ? 'invite someone before changing this' : 'give someone else Full access first'}</>
                          : <>That is you — another Full-access user or Parkify must make this change</>}
                      </div>
                    ) : (
                      <>
                        <select
                          aria-label={`Access level for ${user.email}`}
                          value={resolvedLevel(user)} disabled={rowBusy}
                          onChange={e => void changeLevel(user, e.target.value as TenantAccessLevel)}
                          style={selectStyle}
                        >
                          {LEVELS.map(key => <option key={key} value={key}>{LEVEL_LABEL[key]}</option>)}
                        </select>
                        <button disabled={rowBusy} onClick={() => setRemoveUser(user)} style={removeBtn}>Remove</button>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
            <div style={contactSection}>
              <div style={sectionLabel}>Designated contact</div>
              <div style={{ fontSize: 12.5, color: 'var(--color-gray-600)', lineHeight: 1.5, marginBottom: 9 }}>
                Who Parkify contacts about this account. A specific rotation-notice address still takes priority.
              </div>
              <select
                aria-label="Designated contact"
                value={designatedUser?.email ?? ''}
                disabled={busy === 'contact'}
                onChange={e => void changeDesignatedContact(e.target.value)}
                style={{ ...inputStyle, maxWidth: 420 }}
              >
                <option value="">{me.tenant.contactName ? `${me.tenant.contactName} · contact on file` : 'Contact on file'}</option>
                {users.map(user => (
                  <option key={user.email} value={user.email}>
                    {user.name || user.email} · {LEVEL_LABEL[resolvedLevel(user)]}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </TenantModal>

      {inviteOpen && (
        <TenantModal
          title="Invite someone"
          sub="We'll email them a one-time sign-in link."
          onClose={closeInvite}
          initialFocus={nameRef}
          footer={<>
            <button type="button" onClick={closeInvite} style={secondaryBtn}>Cancel</button>
            <button type="button" disabled={busy === 'invite'} onClick={() => void invite()} style={{ ...primaryBtn, opacity: busy === 'invite' ? 0.6 : 1 }}>{busy === 'invite' ? 'Sending…' : 'Send invite'}</button>
          </>}
        >
          {inviteError && <div style={errorBox}>{inviteError}</div>}
          <Field label="Name" error={fieldErrors.name}>
            {props => <input ref={nameRef} {...props} value={name} onChange={e => { setName(e.target.value); setFieldErrors(prev => ({ ...prev, name: undefined })) }} style={fieldInput(fieldErrors.name)} />}
          </Field>
          <Field label="Email" error={fieldErrors.email}>
            {props => <input {...props} type="email" value={email} onChange={e => { setEmail(e.target.value); setFieldErrors(prev => ({ ...prev, email: undefined })) }} style={fieldInput(fieldErrors.email)} />}
          </Field>
          <Field label="Access level" hint={LEVEL_BLURB[level]}>
            {props => (
              <select {...props} value={level} onChange={e => setLevel(e.target.value as TenantAccessLevel)} style={inputStyle}>
                {LEVELS.map(key => <option key={key} value={key}>{LEVEL_LABEL[key]}</option>)}
              </select>
            )}
          </Field>
        </TenantModal>
      )}

      {removeUser && (
        <TenantModal
          title={`Remove ${removeName}?`}
          sub="They lose access immediately. Their parking, if they have any, is unaffected — a portal login and a parker are different records."
          onClose={closeRemove}
          footer={<>
            <button type="button" onClick={closeRemove} style={secondaryBtn}>Cancel</button>
            <button type="button" disabled={busy === removeUser.email} onClick={() => void remove()} style={primaryBtn}>{busy === removeUser.email ? 'Removing…' : 'Remove access'}</button>
          </>}
        >
          {removeError && <div style={{ ...errorBox, marginBottom: 0 }}>{removeError}</div>}
        </TenantModal>
      )}
    </>
  )
}

function ReadOnlyAccess({ user, me }: { user: PortalUser; me: MeResponse }) {
  const level = resolvedLevel(user)
  const contact = me.tenant.designatedContactName ?? me.tenant.contactName
  const isContact = contact && user.name && contact.trim().toLowerCase() === user.name.trim().toLowerCase()
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14 }}>
        <Avatar text={initials(user)} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>{user.name || user.email}</div>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#6B2F82', marginTop: 2 }}>{LEVEL_LABEL[level]}</div>
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-600)', lineHeight: 1.55 }}>{LEVEL_BLURB[level]}</div>
      <div style={{ borderTop: '1px solid var(--color-gray-200)', paddingTop: 12, marginTop: 14, fontSize: 12.5, fontWeight: 600, color: 'var(--color-gray-600)' }}>
        {contact && !isContact ? `Ask ${contact} to change your access.` : 'Ask a Full-access user to change your access.'}
      </div>
    </div>
  )
}

function Avatar({ text }: { text: string }) {
  return <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(127,63,152,0.12)', color: '#6B2F82', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12, flexShrink: 0 }}>{text}</div>
}

/** A labelled control with either a hint or a per-field error under it (the
 * demo's `TField`). The error is tied to the control by `aria-describedby`. */
function Field({ label, hint, error, children }: {
  label: string
  hint?: string
  error?: string
  children: (control: { id: string; 'aria-invalid'?: true; 'aria-describedby'?: string }) => ReactNode
}) {
  const id = useId()
  const noteId = `${id}-note`
  const note = error || hint
  return (
    <div style={{ marginBottom: 13 }}>
      <label htmlFor={id} style={{ ...labelStyle, ...(error ? { color: errorRed } : null) }}>{label}</label>
      {children({ id, ...(error ? { 'aria-invalid': true as const } : null), ...(note ? { 'aria-describedby': noteId } : null) })}
      {note && <span id={noteId} style={{ display: 'block', fontSize: 11, fontWeight: error ? 700 : 400, color: error ? errorRed : 'var(--color-gray-600)', lineHeight: 1.4, marginTop: 5 }}>{note}</span>}
    </div>
  )
}

const userRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 11, padding: '12px 2px', borderBottom: '1px solid var(--color-gray-200)', flexWrap: 'wrap' }
const errorRed = '#b91c1c'
const inputStyle: CSSProperties = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)', fontSize: 14, background: '#fff' }
const fieldInput = (error?: string): CSSProperties => error ? { ...inputStyle, borderColor: errorRed, background: '#FFFBFB' } : inputStyle
const selectStyle: CSSProperties = { ...inputStyle, width: 156, fontSize: 12, fontWeight: 700, padding: '8px 9px' }
const labelStyle: CSSProperties = { display: 'block', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-gray-600)', marginBottom: 6 }
const primaryBtn: CSSProperties = { ...purpleBtn, padding: '9px 16px', fontSize: 12.5 }
const secondaryBtn: CSSProperties = { ...ghostBtn, padding: '9px 16px', fontSize: 12.5 }
const removeBtn: CSSProperties = { ...secondaryBtn, padding: '7px 10px', color: '#b91c1c', borderColor: 'rgba(185,28,28,0.35)' }
const errorBox: CSSProperties = { background: 'rgba(237,28,36,0.08)', border: '1px solid rgba(237,28,36,0.35)', borderRadius: 10, padding: '10px 12px', marginBottom: 14, color: '#b91c1c', fontSize: 12.5, fontWeight: 600 }
const noticeBox: CSSProperties = { background: 'rgba(98,197,132,0.10)', border: '1px solid rgba(98,197,132,0.4)', borderRadius: 10, padding: '10px 12px', marginBottom: 14, color: '#15803d', fontSize: 12.5, fontWeight: 600 }
const loadErrorBanner: CSSProperties = { background: 'rgba(237,28,36,0.10)', border: '1.5px solid rgba(237,28,36,0.4)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }
const empty: CSSProperties = { padding: '24px 0', textAlign: 'center', color: 'var(--color-gray-600)', fontSize: 13 }
const pill: CSSProperties = { display: 'inline-flex', padding: '2px 7px', borderRadius: 999, background: 'rgba(245,158,11,0.13)', color: '#b45309', fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' }
const contactPill: CSSProperties = { ...pill, background: 'rgba(127,63,152,0.12)', color: '#6B2F82' }
const contactSection: CSSProperties = { borderTop: '1px solid var(--color-gray-200)', marginTop: 8, paddingTop: 16 }
const sectionLabel: CSSProperties = { fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-gray-600)', marginBottom: 7 }
